// Datos de detalle de un pedido de venta: líneas, cargas y solicitudes.
//
// Lo consumen las DOS vistas que existen del mismo pedido —la subtabla de la
// lista (`LineasRow`) y la pantalla dedicada (`PedidoVentaDetallePage`)— para
// que no puedan calcular distinto. Las queryKeys y los staleTime son los de
// siempre: las dos vistas comparten caché y abrir el detalle desde la lista no
// dispara ni una petición de más.
import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { pedidosVentasApi } from '@/services/api'
import {
  agruparPorTransid, pendienteDeLanzar, type ResumenLinea,
} from '@/utils/cargasPicking'
import {
  agruparAlbaranes, albaranesDeFila, albaranesHuerfanos, type ResumenAlbaranes,
} from '@/utils/albaranesLinea'
import type { AlbaranLinea, CargaLinea, PedidoVentaLinea, SolicitudCarga } from '@/types'

interface Opciones {
  /** El pedido admite cargas nuevas (está abierto). Gobierna la consulta de
   *  solicitudes: un pedido que ya no se carga tampoco tiene ninguna en curso, y
   *  esa consulta cruza Fabric y Dataverse, así que no es gratis. */
  puedeCrearCarga?: boolean
}

export interface PedidoVentaDetalle {
  lineas: PedidoVentaLinea[]
  cargas: CargaLinea[]
  albaranes: AlbaranLinea[]
  solicitudes: SolicitudCarga[]

  /** Picking por línea (transid ↔ inventtransid). Trae también en qué cargas
   *  aparece cada línea, en `.cargas`. */
  pickingPorLinea: Map<string, ResumenLinea>
  /** transid → nº de línea del pedido, para los desplegables de carga. */
  lineaPorTransid: Map<string, number | undefined>
  /** transid → descripción del artículo, mismo motivo. */
  descripcionPorTransid: Map<string, string | undefined>

  /** Lo que a una fila le queda por lanzar a una carga.
   *
   *  Se mide contra la LÍNEA (`qtyLinea`), no contra la fila: las cargas cruzan
   *  por `inventtransid`, que es la línea entera, así que incluyen también lo ya
   *  expedido y facturado. Restarlo del pendiente de facturar descontaría dos
   *  veces esa parte. */
  lanzamientoDe: (l: PedidoVentaLinea) => ReturnType<typeof pendienteDeLanzar>

  /** Los albaranes que le tocan a una fila. La regla vive en `albaranesLinea.ts`
   *  y la comparte con el Excel: una fila con factura recibe los de ese par
   *  (línea × factura) y la remanente los que aún no se han facturado. */
  albaranesDe: (l: PedidoVentaLinea) => ResumenAlbaranes | undefined

  /** Las filas todavía sin factura, y las que ya la tienen. Una misma línea de
   *  venta puede estar en las dos: la vista de origen devuelve una fila por
   *  (línea × factura) más otra con el remanente. No se fusionan. */
  sinFacturar: PedidoVentaLinea[]
  facturadas: PedidoVentaLinea[]
  /** Unidades sin facturar que no se han lanzado a ninguna carga. */
  qtySinLanzar: number

  /** Entidad legal del pedido, que sólo consta en sus líneas. */
  empresa?: string

  isLoading: boolean
  isError: boolean
  isFetching: boolean
  refetch: () => void
}

export function usePedidoVentaDetalle(
  salesid: string,
  { puedeCrearCarga }: Opciones = {},
): PedidoVentaDetalle {
  const lineasQ = useQuery({
    queryKey: ['venta-lineas', salesid],
    queryFn: () => pedidosVentasApi.getLineas(salesid),
    staleTime: 5 * 60 * 1000,
  })
  const lineas = useMemo(
    () => (lineasQ.data?.data ?? []) as PedidoVentaLinea[],
    [lineasQ.data],
  )

  // Picking en consulta aparte: no bloquea las líneas y muchos pedidos no
  // tienen cargas, así que su ausencia no debe retrasar el resto.
  const cargasQ = useQuery({
    queryKey: ['venta-cargas', salesid],
    queryFn: () => pedidosVentasApi.getCargas(salesid),
    staleTime: 60 * 1000,
  })
  const cargas = useMemo(
    () => (cargasQ.data?.data ?? []) as CargaLinea[],
    [cargasQ.data],
  )

  // Albaranes, también en consulta aparte y por el mismo motivo que el picking:
  // que falte el nº de albarán no puede retrasar las líneas. Y hay una razón de
  // más aquí — esta consulta lee una VISTA en vivo, mientras las líneas salen de
  // una tabla materializada, así que sus latencias no tienen por qué parecerse.
  const albaranesQ = useQuery({
    queryKey: ['venta-albaranes', salesid],
    queryFn: () => pedidosVentasApi.getAlbaranes(salesid),
    staleTime: 60 * 1000,
  })
  const albaranes = useMemo(
    () => (albaranesQ.data?.data ?? []) as AlbaranLinea[],
    [albaranesQ.data],
  )

  // Cargas pedidas que todavía no existen. Se consultan aparte de las reales
  // porque cambian en cuanto el usuario envía, y las reales tardan ~15 min en
  // moverse. Si la consulta falla no se rompe nada: no se pintan fantasmas y el
  // servidor sigue siendo quien tiene la última palabra sobre el tope.
  const solicitudesQ = useQuery({
    queryKey: ['venta-solicitudes-carga', salesid],
    queryFn: () => pedidosVentasApi.getSolicitudesCarga(salesid),
    staleTime: 30 * 1000,
    retry: 1,
    enabled: !!puedeCrearCarga,
  })
  const solicitudes = useMemo(
    () => (solicitudesQ.data?.data ?? []) as SolicitudCarga[],
    [solicitudesQ.data],
  )

  const pickingPorLinea = useMemo(() => agruparPorTransid(cargas), [cargas])

  const indiceAlbaranes = useMemo(() => agruparAlbaranes(albaranes), [albaranes])
  const albaranesDe = useMemo(
    () => (l: PedidoVentaLinea) => albaranesDeFila(indiceAlbaranes, l),
    [indiceAlbaranes],
  )

  // Centinela de la asimetría entre las dos fuentes: la vista de albaranes es
  // LIVE y la tabla de líneas se reconstruye a mano, así que un albarán puede ir
  // por delante de su línea. No falsea ninguna cifra —el cruce lo conduce la
  // línea, y un huérfano no se pinta—, pero deja un hueco que nadie vería. Aquí
  // es donde las dos fuentes se encuentran, así que es donde se puede avisar sin
  // gastar una consulta de más.
  const huerfanos = useMemo(
    () => albaranesHuerfanos(albaranes, lineas),
    [albaranes, lineas],
  )
  if (huerfanos > 0) {
    console.warn(
      `[albaranes] ${huerfanos} albaranes del pedido ${salesid} no encajan con `
      + 'ninguna línea. La vista de albaranes va por delante de '
      + 'planificacion_pedidos_ventas_lineas, que se reconstruye a mano.',
    )
  }

  const lineaPorTransid = useMemo(() => {
    const m = new Map<string, number | undefined>()
    for (const l of lineas) if (l.inventtransid) m.set(l.inventtransid, l.numLinea)
    return m
  }, [lineas])

  const descripcionPorTransid = useMemo(() => {
    const m = new Map<string, string | undefined>()
    for (const l of lineas) if (l.inventtransid) m.set(l.inventtransid, l.descripcion)
    return m
  }, [lineas])

  const lanzamientoDe = useMemo(
    () => (l: PedidoVentaLinea) => pendienteDeLanzar(
      l.qtyLinea,
      l.inventtransid ? pickingPorLinea.get(l.inventtransid) : undefined,
    ),
    [pickingPorLinea],
  )

  const sinFacturar = useMemo(
    () => lineas.filter(l => !l.factura?.trim()),
    [lineas],
  )
  const facturadas = useMemo(
    () => lineas.filter(l => !!l.factura?.trim()),
    [lineas],
  )

  // Se suma ya recortado a 0, igual que la celda: si se lanzó de más, el exceso
  // no resta aquí.
  const qtySinLanzar = useMemo(
    () => sinFacturar.reduce((s, l) => s + lanzamientoDe(l).pendiente, 0),
    [sinFacturar, lanzamientoDe],
  )

  return {
    lineas,
    cargas,
    albaranes,
    solicitudes,
    pickingPorLinea,
    lineaPorTransid,
    descripcionPorTransid,
    lanzamientoDe,
    albaranesDe,
    sinFacturar,
    facturadas,
    qtySinLanzar,
    empresa: lineas[0]?.dataareaid,
    isLoading: lineasQ.isLoading,
    isError: lineasQ.isError,
    isFetching: lineasQ.isFetching || cargasQ.isFetching || albaranesQ.isFetching,
    refetch: () => {
      void lineasQ.refetch()
      void cargasQ.refetch()
      void albaranesQ.refetch()
      if (puedeCrearCarga) void solicitudesQ.refetch()
    },
  }
}
